import { useRef, useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { motion, useInView } from "framer-motion";
import {
  FileText,
  Activity,
  Calendar,
  Mail,
  Folder,
  Loader2,
  Send,
  Star,
} from "lucide-react";

const API_BASE = "/api";

function AnimatedCard({ children, delay = 0 }: { children: React.ReactNode; delay?: number }) {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-50px" });

  return (
    <motion.div
      ref={ref}
      initial={{ opacity: 0, y: 20 }}
      animate={isInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 20 }}
      transition={{ duration: 0.4, delay }}
    >
      {children}
    </motion.div>
  );
}

function PageHeader() {
  return (
    <AnimatedCard>
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <div className="flex items-center gap-2 mb-2">
            <span className="w-8 h-0.5 bg-accent rounded-full" />
            <span className="text-accent text-xs font-medium uppercase tracking-wide">Overview</span>
          </div>
          <h1 className="text-2xl font-bold text-primary">Dashboard</h1>
          <p className="text-neutral-400 text-sm mt-1">Welcome back to your admin panel</p>
        </div>
        <div className="flex items-center gap-2 text-sm text-neutral-400">
          <Calendar className="w-4 h-4" />
          <span>
            {new Date().toLocaleDateString("en-US", {
              weekday: "long",
              month: "long",
              day: "numeric",
            })}
          </span>
        </div>
      </div>
    </AnimatedCard>
  );
}

function StatsCards() {
  const [stats, setStats] = useState({
    projects: 0,
    messages: 0,
    newMessages: 0,
    emails: 0,
    emailsToday: 0,
    reviews: 0,
    pendingReviews: 0,
  });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchStats();
  }, []);

  const fetchStats = async () => {
    try {
      const [projectsRes, messagesRes, reviewsRes] = await Promise.all([
        fetch(`${API_BASE}/projects`, { headers: { Authorization: `Bearer ${localStorage.getItem('adminToken') || ''}` } }),
        fetch(`${API_BASE}/messages`, { headers: { Authorization: `Bearer ${localStorage.getItem('adminToken') || ''}` } }),
        fetch(`${API_BASE}/reviews?status=all`, { headers: { Authorization: `Bearer ${localStorage.getItem('adminToken') || ''}` } }),
      ]);

      let projects = 0;
      let messages = 0;
      let newMessages = 0;
      let reviews = 0;
      let pendingReviews = 0;

      if (projectsRes.ok) {
        const data = await projectsRes.json();
        projects = Array.isArray(data) ? data.length : 0;
      }
      if (messagesRes.ok) {
        const data = await messagesRes.json();
        if (Array.isArray(data)) {
          messages = data.length;
          newMessages = data.filter((m: { status: string }) => m.status === "New").length;
        }
      }
      if (reviewsRes.ok) {
        const data = await reviewsRes.json();
        if (Array.isArray(data)) {
          reviews = data.length;
          pendingReviews = data.filter((r: { status: string }) => r.status === "pending").length;
        }
      }

      setStats({
        projects,
        messages,
        newMessages,
        emails: 0,
        emailsToday: 0,
        reviews,
        pendingReviews,
      });
    } catch {
      // Silent fail
    } finally {
      setLoading(false);
    }
  };

  const statCards = [
    {
      title: "Total Projects",
      value: stats.projects,
      icon: Folder,
      color: "bg-primary",
      link: "/admin/projects",
    },
    {
      title: "New Messages",
      value: stats.newMessages,
      icon: Mail,
      color: "bg-accent",
      link: "/admin/messages",
    },
    {
      title: "Pending Reviews",
      value: stats.pendingReviews,
      icon: Star,
      color: "bg-yellow-500",
      link: "/admin/reviews",
    },
    {
      title: "Total Reviews",
      value: stats.reviews,
      icon: FileText,
      color: "bg-blue-500",
      link: "/admin/reviews",
    },
  ];

  if (loading) {
    return (
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {[1, 2, 3, 4].map((i) => (
          <div
            key={i}
            className="bg-white rounded-xl p-5 border border-neutral-100 h-24 flex items-center justify-center"
          >
            <Loader2 className="w-5 h-5 text-neutral-300 animate-spin" />
          </div>
        ))}
      </div>
    );
  }

  return (
    <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
      {statCards.map((stat, index) => (
        <AnimatedCard key={stat.title} delay={index * 0.05}>
          <a
            href={stat.link || "#"}
            className="flex items-center gap-4 bg-white rounded-xl p-5 border border-neutral-100 hover:border-neutral-200 transition-colors"
          >
            <div className={`w-12 h-12 ${stat.color} rounded-xl flex items-center justify-center flex-shrink-0`}>
              <stat.icon className="w-6 h-6 text-white" />
            </div>
            <div className="min-w-0">
              <div className="text-2xl font-bold text-primary leading-tight">
                {typeof stat.value === "number" ? stat.value.toLocaleString() : stat.value}
              </div>
              <div className="text-xs text-neutral-400 truncate">{stat.title}</div>
            </div>
          </a>
        </AnimatedCard>
      ))}
    </div>
  );
}

function RecentActivity() {
  const [activities, setActivities] = useState<
    Array<{
      id: string;
      type: string;
      title: string;
      description: string;
      time: string;
      icon: typeof Mail;
      color: string;
      bgColor: string;
    }>
  >([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchRecentActivity();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchRecentActivity = async () => {
    try {
      const [messagesRes, reviewsRes] = await Promise.all([
        fetch(`${API_BASE}/messages`, { headers: { Authorization: `Bearer ${localStorage.getItem('adminToken') || ''}` } }),
        fetch(`${API_BASE}/reviews?status=all`, { headers: { Authorization: `Bearer ${localStorage.getItem('adminToken') || ''}` } }),
      ]);

      let allActivities: typeof activities = [];

      if (messagesRes.ok) {
        const messages = await messagesRes.json();
        const messageActivities = messages.slice(0, 5).map((msg: { id: number; name: string; message: string; created_at: string }) => ({
          id: `msg-${msg.id}`,
          type: "message",
          title: `New message from ${msg.name}`,
          description: msg.message.substring(0, 60) + (msg.message.length > 60 ? "..." : ""),
          time: msg.created_at,
          icon: Mail,
          color: "text-accent",
          bgColor: "bg-accent/10",
        }));
        allActivities = [...allActivities, ...messageActivities];
      }

      if (reviewsRes.ok) {
        const reviews = await reviewsRes.json();
        const reviewActivities = reviews
          .filter((r: { status: string }) => r.status === "pending")
          .slice(0, 3)
          .map((review: { id: number; name: string; comment: string; created_at: string }) => ({
            id: `review-${review.id}`,
            type: "review",
            title: `Review pending from ${review.name}`,
            description: review.comment.substring(0, 60) + (review.comment.length > 60 ? "..." : ""),
            time: review.created_at,
            icon: Star,
            color: "text-yellow-500",
            bgColor: "bg-yellow-500/10",
          }));
        allActivities = [...allActivities, ...reviewActivities];
      }

      allActivities.sort((a, b) => new Date(b.time).getTime() - new Date(a.time).getTime());
      setActivities(allActivities.slice(0, 6));
    } catch {
      // Silent fail
    } finally {
      setLoading(false);
    }
  };

  const formatTime = (dateString: string) => {
    if (!dateString) return "Recently";
    const date = new Date(dateString);
    const now = new Date();
    const diff = (now.getTime() - date.getTime()) / 1000;

    if (diff < 60) return "Just now";
    if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
    if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
    return `${Math.floor(diff / 86400)}d ago`;
  };

  return (
    <AnimatedCard delay={0.4}>
      <div className="bg-white rounded-xl border border-neutral-100 p-6">
        <div className="flex items-center justify-between mb-5">
          <h2 className="text-base font-semibold text-primary">Recent Activity</h2>
          <Activity className="w-4 h-4 text-neutral-400" />
        </div>

        <div className="space-y-3">
          {loading ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="w-5 h-5 text-neutral-300 animate-spin" />
            </div>
          ) : activities.length > 0 ? (
            activities.map((activity) => (
              <motion.div
                key={activity.id}
                initial={{ opacity: 0, x: -10 }}
                animate={{ opacity: 1, x: 0 }}
                className="flex items-start gap-3 p-3 rounded-xl hover:bg-neutral-50 transition-colors"
              >
                <div className={`w-9 h-9 ${activity.bgColor} rounded-lg flex items-center justify-center flex-shrink-0`}>
                  <activity.icon className={`w-4 h-4 ${activity.color}`} />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-medium text-primary truncate">{activity.title}</div>
                  <div className="text-xs text-neutral-400 line-clamp-1">{activity.description}</div>
                </div>
                <div className="text-xs text-neutral-300 flex-shrink-0">{formatTime(activity.time)}</div>
              </motion.div>
            ))
          ) : (
            <div className="text-center py-8 text-neutral-400 text-sm">
              <Activity className="w-8 h-8 mx-auto mb-2 text-neutral-200" />
              <p>No recent activity</p>
            </div>
          )}
        </div>
      </div>
    </AnimatedCard>
  );
}

function QuickActions() {
  const actions = [
    { title: "Add Project", icon: Folder, href: "/admin/projects/new", color: "bg-primary" },
    { title: "View Messages", icon: Mail, href: "/admin/messages", color: "bg-accent" },
    { title: "Manage Reviews", icon: Star, href: "/admin/reviews", color: "bg-yellow-500" },
  ];

  return (
    <AnimatedCard delay={0.5}>
      <div className="bg-white rounded-xl border border-neutral-100 p-6">
        <h2 className="text-base font-semibold text-primary mb-5">Quick Actions</h2>
        <div className="space-y-3">
          {actions.map((action) => (
            <a
              key={action.title}
              href={action.href}
              className="flex items-center gap-4 p-4 rounded-xl border border-neutral-100 hover:border-accent/30 hover:bg-accent/5 transition-all"
            >
              <div className={`w-11 h-11 ${action.color} rounded-xl flex items-center justify-center`}>
                <action.icon className="w-5 h-5 text-white" />
              </div>
              <span className="text-sm font-medium text-neutral-700">{action.title}</span>
            </a>
          ))}
        </div>
      </div>
    </AnimatedCard>
  );
}

export default function AdminDashboard() {
  return (
    <div className="space-y-6">
      <PageHeader />
      <StatsCards />

      <div className="grid lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2">
          <RecentActivity />
        </div>
        <div>
          <QuickActions />
        </div>
      </div>
    </div>
  );
}
